import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { isAuthenticated } from '../utils/authHelper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9088/api';

const Subscription = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null); // Track selected plan
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  const billingPeriods = [
    { value: 'monthly', label: 'Monthly', shortLabel: 'mo' },
    { value: 'quarterly', label: 'Quarterly', shortLabel: 'quarter' },
    { value: 'yearly', label: 'Annually', shortLabel: 'year' }
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    // Set default selected plan to Basic when plans are loaded
    if (plans.length > 0 && !selectedPlan) {
      const basicPlan = plans.find(p => p.slug === 'basic');
      if (basicPlan) {
        setSelectedPlan(basicPlan.id);
      }
    }
  }, [plans, selectedPlan]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/subscription/plans`);
      if (response.data.success) {
        setPlans(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      // Error is acceptable - user can still view the page structure
    } finally {
      setLoading(false);
    }
  };

  const getPriceForPeriod = (plan, billingPeriod) => {
    if (!plan.prices || plan.prices.length === 0) return null;
    return plan.prices.find(p => p.billing_period === billingPeriod);
  };

  const isCurrentPlan = (planSlug) => {
    return user?.subscription?.plan_name?.toLowerCase() === planSlug?.toLowerCase();
  };

  const getPlanFeatures = (plan) => {
    // Free Plan Features
    if (plan.slug === 'free') {
      return [
        'Unlimited AI Conversations',
        'Basic AI Models',
        'Creative Ad Generation - AI SEO',
        'Email writing assistant',
        'Product Descriptions',
        'Save generation history',
        "Zest's Blog"
      ];
    }

    // Basic Plan Features
    if (plan.slug === 'basic') {
      return [
        'Unlimited AI Conversations',
        'All AI Models',
        'Creative Ad Generation - AI SEO',
        'Email writing assistant',
        'Product Descriptions',
        'Blog Posters Generation',
        'Poster',
        'AI Image generation',
        'Save generation history',
        "Zest's Blog"
      ];
    }

    // Professional Plan Features
    if (plan.slug === 'professional') {
      return [
        'Unlimited AI Conversations',
        'All AI Models',
        'Creative Ad Generation - AI SEO',
        'Email writing assistant',
        'Product Descriptions',
        'Blog Posters Generation',
        'Poster',
        'AI Image generation',
        'Market Survey',
        'Image Composition',
        'Speech Synthesis',
        'Save generation history',
        "Zest's Blog"
      ];
    }

    // Fallback for unknown plans
    return [
      `${plan.monthly_generation_limit} generations per month`,
      'All content types',
      'Save to history'
    ];
  };

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
  };

  const isRenewal = () => {
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan || !user?.subscription) return false;

    return (
      plan.slug.toLowerCase() === user.subscription.plan_name?.toLowerCase() &&
      selectedBillingPeriod === user.subscription.billing_period
    );
  };

  const getActionType = () => {
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return 'subscribe';

    if (isCurrentPlan(plan.slug)) {
      if (selectedBillingPeriod === user.subscription.billing_period) {
        return 'renew'; // Same plan, same period
      } else {
        return 'change'; // Same plan, different period
      }
    }

    // Different plan
    const currentPlanIndex = plans.findIndex(p => isCurrentPlan(p.slug));
    const selectedPlanIndex = plans.findIndex(p => p.id === selectedPlan);

    if (currentPlanIndex < selectedPlanIndex) {
      return 'upgrade';
    } else {
      return 'downgrade';
    }
  };

  const handleContinueToPayment = () => {
    // Check if user is logged in
    if (!isAuthenticated()) {
      // Redirect to login page
      window.location.href = '/login';
      return;
    }

    const plan = plans.find(p => p.id === selectedPlan);
    const price = getPriceForPeriod(plan, selectedBillingPeriod);
    const actionType = getActionType();

    if (!plan || !price || !price.stripe_price_id) {
      alert('Please select a valid plan with payment configured.');
      return;
    }

    // For free plan, skip Stripe
    if (plan.slug === 'free') {
      alert('Free plan does not require payment.');
      return;
    }

    const billingLabel = billingPeriods.find(p => p.value === selectedBillingPeriod)?.label;

    // Prepare confirmation data
    let confirmData = {
      plan,
      price,
      billingLabel,
      actionType
    };

    if (actionType === 'renew') {
      const extendedDate = new Date(user.subscription.current_period_end);
      switch(selectedBillingPeriod) {
        case 'monthly':
          extendedDate.setMonth(extendedDate.getMonth() + 1);
          break;
        case 'quarterly':
          extendedDate.setMonth(extendedDate.getMonth() + 3);
          break;
        case 'yearly':
          extendedDate.setFullYear(extendedDate.getFullYear() + 1);
          break;
        default:
          break;
      }
      confirmData.extendedDate = extendedDate;
    } else if (actionType === 'downgrade' || actionType === 'change') {
      confirmData.effectiveDate = new Date(user.subscription.current_period_end);
    }

    // Show confirmation modal
    setConfirmationData(confirmData);
    setShowConfirmModal(true);
  };

  const handleConfirmPayment = async () => {
    try {
      setShowConfirmModal(false);

      // Create Stripe Checkout Session
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/stripe/create-checkout-session`,
        {
          price_id: confirmationData.price.stripe_price_id,
          plan_id: confirmationData.plan.id,
          billing_period: selectedBillingPeriod
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success && response.data.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.url;
      } else {
        alert('Failed to initiate payment. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(`Error: ${error.response?.data?.error?.message || 'Failed to initiate payment'}`);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading plans...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-12">
            {/* Header Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-3">Choose Your Plan</h1>
              <p className="text-lg text-gray-600">Select the perfect plan for your needs</p>
            </div>

            {/* Billing Period Toggle */}
            <div className="flex justify-center mb-12">
              <div className="inline-flex bg-gray-100 rounded-lg p-1">
                {billingPeriods.map((period) => (
                  <button
                    key={period.value}
                    onClick={() => setSelectedBillingPeriod(period.value)}
                    className={`px-8 py-2.5 rounded-md text-sm font-semibold transition-all ${
                      selectedBillingPeriod === period.value
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {period.label}
                    {period.value === 'yearly' && (
                      <span className="ml-2 inline-block text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                        Save 17%
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {plans.map((plan) => {
                const price = getPriceForPeriod(plan, selectedBillingPeriod);
                const isCurrent = isCurrentPlan(plan.slug);
                const isSelected = selectedPlan === plan.id;
                const isPopular = plan.slug === 'basic';
                const features = getPlanFeatures(plan);
                const canSelect = price && plan.slug !== 'free';

                return (
                  <div
                    key={plan.id}
                    onClick={() => canSelect && handleSelectPlan(plan.id)}
                    className={`bg-white rounded-2xl shadow-sm relative transition-all cursor-pointer ${
                      isSelected && canSelect
                        ? 'ring-4 ring-blue-500 transform scale-105'
                        : isPopular && !isSelected
                        ? 'ring-2 ring-blue-200'
                        : 'border border-gray-200 hover:border-gray-300'
                    } ${canSelect ? 'hover:shadow-lg' : 'cursor-default'}`}
                  >
                    {/* Selection Indicator */}
                    {isSelected && canSelect && (
                      <div className="absolute -top-3 -right-3 bg-blue-600 rounded-full p-2 shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}

                    {isPopular && !isSelected && (
                      <div className="absolute -top-5 left-0 right-0 flex justify-center">
                        <span className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                          Most Popular
                        </span>
                      </div>
                    )}

                    {isCurrent && (
                      <div className="absolute top-6 right-6">
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                          Current Plan
                        </span>
                      </div>
                    )}

                    <div className="p-8">
                      {/* Plan Header */}
                      <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>

                        {price ? (
                          <div className="mt-4">
                            <div className="flex items-baseline justify-center">
                              <span className="text-5xl font-bold text-gray-900">
                                ${parseFloat(price.price_usd).toFixed(2)}
                              </span>
                              <span className="text-gray-600 ml-2">
                                /{billingPeriods.find(p => p.value === selectedBillingPeriod)?.shortLabel}
                              </span>
                            </div>
                            {price.discount_percentage > 0 && selectedBillingPeriod !== 'monthly' && (
                              <div className="mt-2">
                                <span className="text-sm text-green-600 font-semibold">
                                  Save {price.discount_percentage}%
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="mt-4">
                            <span className="text-gray-500 text-sm">
                              {plan.slug === 'free' ? 'Always Free' : 'Not available'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Features List */}
                      <ul className="space-y-4 mb-8">
                        {features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Selection Status */}
                      <div className="text-center">
                        {isCurrent ? (
                          <div className="py-3 text-gray-500 text-sm font-medium">
                            Your Current Plan
                          </div>
                        ) : plan.slug === 'free' ? (
                          <div className="py-3 text-gray-500 text-sm font-medium">
                            Always Free
                          </div>
                        ) : !price ? (
                          <div className="py-3 text-gray-500 text-sm font-medium">
                            Not Available
                          </div>
                        ) : isSelected ? (
                          <div className="py-3 text-blue-600 text-sm font-semibold">
                            ✓ Selected
                          </div>
                        ) : (
                          <div className="py-3 text-gray-400 text-sm font-medium">
                            Click to select
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Continue to Payment Button */}
            <div className="max-w-6xl mx-auto mb-16">
              <div className="text-center">
                <button
                  onClick={handleContinueToPayment}
                  disabled={!selectedPlan || plans.find(p => p.id === selectedPlan)?.slug === 'free'}
                  className={`px-12 py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${
                    selectedPlan && plans.find(p => p.id === selectedPlan)?.slug !== 'free'
                      ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl transform hover:scale-105'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {(() => {
                    const actionType = getActionType();
                    switch(actionType) {
                      case 'renew':
                        return 'Renew Subscription';
                      case 'upgrade':
                        return 'Upgrade Plan';
                      case 'downgrade':
                        return 'Change Plan';
                      case 'change':
                        return 'Change Billing Period';
                      default:
                        return 'Continue to Payment';
                    }
                  })()}
                </button>
                {selectedPlan && (
                  <div className="mt-4 text-sm text-gray-600">
                    <p className="font-medium">
                      {plans.find(p => p.id === selectedPlan)?.name} - {billingPeriods.find(p => p.value === selectedBillingPeriod)?.label}
                    </p>
                    {getActionType() === 'renew' && user?.subscription?.current_period_end && (
                      <p className="mt-2 text-green-600">
                        Will extend your subscription through {new Date(new Date(user.subscription.current_period_end).setMonth(
                          new Date(user.subscription.current_period_end).getMonth() +
                          (selectedBillingPeriod === 'monthly' ? 1 : selectedBillingPeriod === 'quarterly' ? 3 : 12)
                        )).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* FAQ or Additional Info */}
            <div className="text-center mt-12">
              <p className="text-gray-600">
                Need help choosing? <a href="/contact" className="text-blue-600 hover:text-blue-700 font-semibold">Contact our sales team</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && confirmationData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {confirmationData.actionType === 'renew' && 'Renew Subscription'}
                {confirmationData.actionType === 'upgrade' && 'Upgrade Plan'}
                {confirmationData.actionType === 'downgrade' && 'Change Plan'}
                {confirmationData.actionType === 'change' && 'Change Billing Period'}
                {confirmationData.actionType === 'subscribe' && 'Subscribe to Plan'}
              </h3>
              <p className="text-gray-600">
                {confirmationData.plan.name} - {confirmationData.billingLabel}
              </p>
            </div>

            {/* Details */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-gray-600">Amount</span>
                <span className="text-3xl font-bold text-gray-900">
                  ${parseFloat(confirmationData.price.price_usd).toFixed(2)}
                </span>
              </div>

              {confirmationData.actionType === 'renew' && confirmationData.extendedDate && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Subscription extended until</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {confirmationData.extendedDate.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}

              {confirmationData.actionType === 'upgrade' && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-green-600 font-medium flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                    </svg>
                    Starts immediately
                  </p>
                </div>
              )}

              {(confirmationData.actionType === 'downgrade' || confirmationData.actionType === 'change') && confirmationData.effectiveDate && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-amber-600 font-medium flex items-center mb-2">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Effective from
                  </p>
                  <p className="text-sm font-semibold text-gray-900 ml-5">
                    {confirmationData.effectiveDate.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                  {confirmationData.actionType === 'downgrade' && user?.subscription?.plan_name && (
                    <p className="text-xs text-gray-500 mt-2 ml-5">
                      Keep access to {user.subscription.plan_name} until then
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-sm text-blue-800 flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                You'll be redirected to Stripe's secure checkout to complete payment
              </p>
            </div>

            {/* Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPayment}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
